import type { BrandConfig } from '../types';
import logo from '@/assets/images/logo.svg';
import logoWhite from '@/assets/images/logo-white.svg';
import whatIsIncluded1 from '@/assets/images/what-is-included-1.png';
import whatIsIncluded2 from '@/assets/images/what-is-included-2.png';
import whatIsIncluded3 from '@/assets/images/what-is-included-3.png';
import customerFeedbackImg from '@/assets/images/customer-feedback.png';
import consultationIcon from '@/assets/images/consultation-banner-icon.svg';

export const glfConfig: BrandConfig = {
  name: 'glf',
  brand: 'GLF',

  // Meta
  title: 'Law Firm Marketing Assessment',
  favicon: '/favicon.ico',

  // Header
  header: {
    logo: logo,
    siteName: 'Law Firm <strong>Marketing Assessment</strong>',
    phone: '(954) 859-1712',
    ctaText: 'Request Consultation',
    ctaLink: '/request-consultation',
  },

  // Footer
  footer: {
    logo: logoWhite,
    companyName: 'Grow Law',
    tagline: 'Grow your law firm',
    phone: '(954) 859-1712',
    email: 'info@growlaw.co',
    address: '501 E Las Olas Blvd, Suite 300, Fort Lauderdale, FL 33301',
    addressUrl: 'https://maps.app.goo.gl/RMYE7W4hBHpjL2z26',
    directionsUrl: 'https://maps.app.goo.gl/k9aGaWrsz8cqbXoy9',
    disclaimer:
      '*This Company is not a law firm and does not provide legal services. Let us know if you would like to discuss this further.',
    copyright: `© ${new Date().getFullYear()} Grow Law`,
    columns: [
      {
        title: 'Services',
        links: [
          {
            label: 'Law firm Website Design',
            url: 'https://growlaw.co/law-firm-website-design',
          },
          {
            label: 'SEO Services for Attorneys',
            url: 'https://growlaw.co/law-firm-seo',
          },
          {
            label: 'PPC Ads for Lawyers',
            url: 'https://growlaw.co/law-firm-ppc',
          },
        ],
      },
      {
        title: 'Law Firms We Help',
        links: [
          {
            label: 'Personal Injury',
            url: 'https://growlaw.co/personal-injury-marketing-services',
          },
          {
            label: 'Criminal Defense',
            url: 'https://growlaw.co/criminal-defense-marketing-services',
          },
          {
            label: 'Family Law',
            url: 'https://growlaw.co/family-law-marketing-services',
          },
          {
            label: 'Immigration',
            url: 'https://growlaw.co/immigration-law-marketing-services',
          },
          {
            label: 'Estate Planning & Probate',
            url: 'https://growlaw.co/estate-planning-marketing-services',
          },
        ],
      },
      {
        title: 'About Us',
        links: [
          { label: 'Law Firm Academy', url: 'https://growlaw.co/academy' },
          { label: 'Blog', url: 'https://growlaw.co/blog' },
          { label: 'Results', url: 'https://growlaw.co/results' },
          {
            label: 'Careers',
            url: 'https://growlaw.co/careers/relationship-manager',
          },
        ],
      },
    ],
    bottomLinks: [
      { label: 'Privacy Policy', url: 'https://growlaw.co/privacy-policy' },
      { label: 'Terms of Use', url: 'https://growlaw.co/terms-of-use' },
    ],
    social: [
      { icon: 'facebook', url: 'https://www.facebook.com/growlaw' },
      { icon: 'linkedin', url: 'https://www.linkedin.com/company/grow-law/' },
      { icon: 'instagram', url: 'https://www.instagram.com/grow_law/' },
      { icon: 'youtube', url: 'https://www.youtube.com/@growlaw' },
    ],
  },

  // Landing sections — matches the order rendered in HomeView.vue step 1
  landing: {
    step1Sections: [
      { component: 'CheckPerformance' },
      {
        component: 'CompareYourFirmForm',
        props: { autocompleteId: 'map1', id: 'compare-form-top' },
      },
      {
        component: 'HowItWorks',
        props: { videoLink: 'https://www.youtube.com/watch?v=NVzMkIjZAqo' },
      },
      { component: 'WhatIsIncluded' },
      { component: 'CustomerFeedback' },
      {
        component: 'CompareYourFirmForm',
        props: {
          autocompleteId: 'map2',
          id: 'compare-form',
          title:
            "Get a clear picture of your firm's performance and actionable ideas for improving your online presence",
        },
      },
    ],
    step2Sections: [
      { component: 'ContactInformation' },
      { component: 'GrowthFactorsList' },
    ],
  },

  // Hero section (CheckPerformance component)
  hero: {
    title: 'Get your personalized marketing assessment in minutes',
    badge: 'Free',
    description:
      'Instantly see how your firm stacks up against top competitors in your area and gain actionable insights to attract ideal clients.',
  },

  // HowItWorks section
  howItWorks: {
    title: 'Why use a law firm marketing assessment tool',
    paragraphs: [
      '<strong>80% of potential clients search for legal services online. Is your website winning them over?</strong>',
      "Our Law Firm Marketing Assessment Tool analyzes your website against top competitors to uncover what's working and what isn't—to attract clients.",
    ],
    videoLink: 'https://www.youtube.com/watch?v=NVzMkIjZAqo',
  },

  // WhatIsIncluded section
  whatIsIncluded: {
    subtitle: "WHAT's included",
    title: 'You will receive real-time assessment for the following parameters',
    buttonText: 'Compare your firm',
    features: [
      {
        title: 'Find out where your firm ranks on Google for relevant keywords',
        description:
          'Web presence score scans where you appear in Google search results when potential clients search for your practice area in your local area.',
        image: whatIsIncluded1,
      },
      {
        title: "See your firm's reputation on review sites",
        description:
          'The reputation score is calculated based on the average star rating and the total number of reviews.',
        image: whatIsIncluded2,
      },
      {
        title: "Assess your website's overall performance",
        description:
          "The website performance score is derived from an analysis of your website's loading speed, security, optimization, and user-friendliness.",
        image: whatIsIncluded3,
      },
    ],
  },

  // Customer feedback section
  customerFeedback: {
    title: 'Customer feedback',
    quote:
      "The Marketing Assessment tool provided instant clarity on our marketing success, something I never found in detailed reports. It's the simplest and quickest way to understand our marketing performance.",
    author: 'SANDRA VANCOV, Attorney At Law',
    rating: 5,
    image: customerFeedbackImg,
  },

  // Form (CompareYourFirmForm component)
  form: {
    businessLabel: 'Enter your firm name',
    businessTooltip: 'Start typing the name of your law firm',
    practiceLabel: 'Choose your main practice area',
    practiceTooltip:
      'Select your practice area to compare against main competitors.',
    buttonText: 'Compare your firm',
    searchSuffix: 'law firm',
    searchExample: 'lawyer near me',
    bottomTitle:
      "Get a clear picture of your firm's performance and actionable ideas for improving your online presence",
    roles: [
      'Principal/Partner',
      'Marketing Director',
      'Associate',
      'Paralegal',
    ],
  },

  // Consultation banner
  consultation: {
    title: 'Request Consultation',
    description:
      "Schedule a complimentary call to discuss your firm's most pressing issues and marketing goals.",
    buttonText: 'Request Consultation',
    image: consultationIcon,
    pageTitle:
      "Schedule a complimentary call to discuss your firm's most pressing issues and marketing goals.",
  },

  // Education / checklist section
  education: {
    sectionTitle:
      'Boost Your Rankings and Get More Cases with These Actionable Steps:',
    reputationGoodText:
      'Your law firm has done good job collection reviews, but there are competitors whose average rating is higher than yours. Try ' +
      'to increase your average rating to 5 stars. This not only boosts your online reputation but also positions ' +
      'you among the top 10 local listings, notably on platforms like Google Maps.',
    reputationGreatText:
      'Your law firm\u2019s average rating is exceptional, while there are competitors who have more reviews. Aim ' +
      'to add {count} + new positive reviews on Google. This not only boosts your online reputation but also ' +
      'positions you among the top 10 local listings, notably on platforms like Google Maps.',
    showcaseAchievementsText:
      'Showcase achievements: Display notable accolades such as Avvo awards, Super Lawyers recognitions, etc.',
  },

  // Dashboard session banner (used across OverallScore, WebPresence, FirmReputation, WebsitePerformance, WebsiteAuthority)
  sessionBanner: {
    title: 'Book A Free Strategy Session <span>with our Team</span>',
    text: '<strong>Looking to grow your business? Need more sales?</strong> Our team of experts will work closely with you to build customized marketing strategy tailored to your law firm',
    items: ['Reach new customers', 'Boost sales', 'Hit your financial goals'],
  },

  // HubSpot
  hubspot: {
    formId: '24fa7142-7763-4a14-9c01-eae07e58b2d2',
    companyNameField: 'firm_name',
    companyUrlField: 'website',
    roleField: 'role_at_law_firm',
    practiceAreaField: 'type_of_law_firm',
    employeeCountField: 'number_of_attorneys',
    includeDomain: true,
    includeLandingPage: true,
  },

  // HubSpot Meetings
  meeting: {
    url: 'https://meetings.hubspot.com/mary-grinnan/meet-with-grow-law-expert-marketing-assessment',
  },

  // PDF report texts
  pdf: {
    heroTitle: 'Discover hidden opportunities to grow your law firm',
    nextStepsTitle: 'Your next client is searching for a lawyer right now',
    heroDescription:
      "In this report, you will see how your law firm's marketing stacks up to your top competitors and how you can improve your marketing performance and get more leads and new cases.",
    practiceNoun: 'lawyer',
    ctaTitle:
      'Get a growth plan custom-tailored for your practice - <br /> a $499 value—completely free!',
    nextStepsAuthorTitle: 'Law Firm Growth Expert',
    nextStepsBrandTitle: 'Brought to you by Grow Law',
    nextStepsBrandDescription:
      'Grow Law helps law firms achieve rapid and sustained growth through SEO, PPC and website design services high-performance web design services.',
    reviewsIntroHeading:
      '84% of surveyed law firm owners indicated that increasing their revenue is their top priority.',
    reviewsBodyLines: [
      'If revenue is your top priority as well, you know the only way to achieve this is by either increasing your caseload or raising prices (not always a great option), or some combination of the two.',
      'Increasing the number of cases your firm takes on is the most reliable way to grow, but most firms struggle with actually making this happen.',
      "To get more cases, you need to attract more clients. <strong>While 59% of all clients sought a referral to a lawyer, 57% searched online, and 16% did both.</strong><sup>2</sup> Given these statistics, it's clear your online presence should be a major case generator for your firm.",
      '<strong>The following report, created specifically for your firm, reveals</strong>',
    ],
    reviewsBulletLines: [
      'The number of searches in your geographical market for lawyers in your practice area',
      'How your website performs at attracting demand, expressed as search traffic',
      'Untapped opportunities to get more clients',
    ],
    tocItems: [
      {
        title: 'Overall Firm Score',
        description:
          'See the aggregate score for your website and web presence',
      },
      {
        title: 'Web Presence',
        description:
          'See if prospects are more likely to find your firm or those of your competitors',
      },
      {
        title: 'Firm\u2019s Reputation',
        description:
          'Do the number and quality of reviews put your firm ahead of the competition?',
      },
      {
        title: 'Website Performance',
        description:
          "Does your website's technical performance get in the way of getting more clients?",
      },
      {
        title: 'Website Authority',
        description:
          'The number and quality of backlinks indicate whether your site is thrust-worthy. See how your website stacks up against the competition.',
      },
      {
        title: 'Next Steps',
        description:
          'Steps to take now to grow the number of cases you attract.',
      },
    ],
  },
};
